import { supabase } from './supabase';

// -------------------------------------------------------------------
// Dashboard Data Fetching — queries Supabase for analytics
// All functions accept optional propertyId for per-property filtering
// -------------------------------------------------------------------

export interface OverviewMetrics {
    uniqueVisitors: number;
    totalSessions: number;
    totalSubmissions: number;
    avgDuration: number;
    conversionRate: number;
}

export interface TimeSeriesPoint {
    date: string;
    visitors: number;
    sessions: number;
}

export interface DeviceBreakdown {
    desktop: number;
    mobile: number;
    tablet: number;
}

export interface GeoEntry {
    country: string;
    country_code: string;
    count: number;
}

export interface FormSubmission {
    id: string;
    form_type: string;
    name: string;
    email: string;
    phone: string | null;
    preferred_month: string | null;
    budget_range: string | null;
    country: string | null;
    device_type: string | null;
    created_at: string;
    lead_score?: number;
}

export interface EngagementMetrics {
    avgSessionDuration: number;
    totalHotspotClicks: number;
    totalCameraMoves: number;
    totalApartmentsViewed: number;
    totalFormsOpened: number;
    totalRoofToggles: number;
    totalHdrChanges: number;
}

export interface FunnelData {
    totalVisitors: number;
    engaged: number;
    formViewers: number;
    converted: number;
}

function getSinceDate(days: number): string {
    if (days <= 0) return '2020-01-01T00:00:00.000Z'; // "all time"
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString();
}

// -------------------------------------------------------------------
// Overview Cards
// -------------------------------------------------------------------

export async function fetchOverviewMetrics(days: number, propertyId?: string | null): Promise<OverviewMetrics> {
    const since = getSinceDate(days);

    let sessionsQuery = supabase
        .from('villa_visitor_sessions')
        .select('visitor_id, duration_seconds, is_engaged, is_converted, forms_submitted')
        .gte('started_at', since);

    let submissionsQuery = supabase
        .from('villa_form_submissions')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', since);

    if (propertyId) {
        sessionsQuery = sessionsQuery.eq('property_id', propertyId);
        submissionsQuery = submissionsQuery.eq('property_id', propertyId);
    }

    const [sessionsRes, submissionsRes] = await Promise.all([sessionsQuery, submissionsQuery]);

    const sessions = sessionsRes.data || [];
    const totalSubmissions = submissionsRes.count || 0;

    const uniqueVisitors = new Set(sessions.map((s: any) => s.visitor_id)).size;
    const totalSessions = sessions.length;
    const avgDuration = totalSessions > 0
        ? sessions.reduce((sum: number, s: any) => sum + (s.duration_seconds || 0), 0) / totalSessions
        : 0;
    const conversions = sessions.filter((s: any) => s.is_converted).length;
    const conversionRate = totalSessions > 0 ? (conversions / totalSessions) * 100 : 0;

    return { uniqueVisitors, totalSessions, totalSubmissions, avgDuration, conversionRate };
}

// -------------------------------------------------------------------
// Visitor Time Series
// -------------------------------------------------------------------

export async function fetchVisitorTimeSeries(days: number, propertyId?: string | null): Promise<TimeSeriesPoint[]> {
    const since = getSinceDate(days);

    let query = supabase
        .from('villa_visitor_sessions')
        .select('visitor_id, started_at')
        .gte('started_at', since)
        .order('started_at', { ascending: true });

    if (propertyId) {
        query = query.eq('property_id', propertyId);
    }

    const { data: sessions } = await query;

    if (!sessions || sessions.length === 0) return [];

    // Group by date
    const byDate = new Map<string, { visitors: Set<string>; sessions: number }>();

    sessions.forEach((s: any) => {
        const date = new Date(s.started_at).toISOString().split('T')[0];
        if (!byDate.has(date)) {
            byDate.set(date, { visitors: new Set(), sessions: 0 });
        }
        const entry = byDate.get(date)!;
        entry.visitors.add(s.visitor_id);
        entry.sessions++;
    });

    // Fill in missing dates
    const result: TimeSeriesPoint[] = [];
    const startDate = new Date(since);
    const endDate = new Date();

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        const entry = byDate.get(dateStr);
        result.push({
            date: dateStr,
            visitors: entry ? entry.visitors.size : 0,
            sessions: entry ? entry.sessions : 0,
        });
    }

    return result;
}

// -------------------------------------------------------------------
// Device Breakdown
// -------------------------------------------------------------------

export async function fetchDeviceBreakdown(days: number, propertyId?: string | null): Promise<DeviceBreakdown> {
    const since = getSinceDate(days);

    let query = supabase
        .from('villa_visitor_sessions')
        .select('device_type')
        .gte('started_at', since);

    if (propertyId) {
        query = query.eq('property_id', propertyId);
    }

    const { data: sessions } = await query;

    const breakdown: DeviceBreakdown = { desktop: 0, mobile: 0, tablet: 0 };

    (sessions || []).forEach((s: any) => {
        const dt = (s.device_type || 'desktop').toLowerCase();
        if (dt === 'mobile') breakdown.mobile++;
        else if (dt === 'tablet') breakdown.tablet++;
        else breakdown.desktop++;
    });

    return breakdown;
}

// -------------------------------------------------------------------
// Geographic Data
// -------------------------------------------------------------------

export async function fetchGeoData(days: number, propertyId?: string | null): Promise<GeoEntry[]> {
    const since = getSinceDate(days);

    let query = supabase
        .from('villa_visitor_sessions')
        .select('country, country_code')
        .gte('started_at', since);

    if (propertyId) {
        query = query.eq('property_id', propertyId);
    }

    const { data: sessions } = await query;

    const countMap = new Map<string, { country_code: string; count: number }>();

    (sessions || []).forEach((s: any) => {
        const country = s.country || 'Unknown';
        if (!countMap.has(country)) {
            countMap.set(country, { country_code: s.country_code || '', count: 0 });
        }
        countMap.get(country)!.count++;
    });

    return Array.from(countMap.entries())
        .map(([country, data]) => ({ country, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
}

// -------------------------------------------------------------------
// Referrer Sources
// -------------------------------------------------------------------

export async function fetchReferrerSources(days: number, propertyId?: string | null): Promise<{ source: string; count: number }[]> {
    const since = getSinceDate(days);

    let query = supabase
        .from('villa_visitor_sessions')
        .select('referrer, utm_source')
        .gte('started_at', since);

    if (propertyId) {
        query = query.eq('property_id', propertyId);
    }

    const { data: sessions } = await query;

    const countMap = new Map<string, number>();

    (sessions || []).forEach((s: any) => {
        let source = s.utm_source || '';
        if (!source && s.referrer) {
            try {
                source = new URL(s.referrer).hostname;
            } catch {
                source = s.referrer;
            }
        }
        if (!source) source = 'Direct';

        countMap.set(source, (countMap.get(source) || 0) + 1);
    });

    return Array.from(countMap.entries())
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
}

// -------------------------------------------------------------------
// Form Submissions
// -------------------------------------------------------------------

export async function fetchFormSubmissions(
    days: number,
    search: string = '',
    page: number = 0,
    pageSize: number = 20,
    propertyId?: string | null
): Promise<{ data: FormSubmission[]; total: number }> {
    const since = getSinceDate(days);

    let query = supabase
        .from('villa_form_submissions')
        .select('*', { count: 'exact' })
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

    if (propertyId) {
        query = query.eq('property_id', propertyId);
    }

    if (search) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%`);
    }

    const { data, count } = await query;

    // Calculate lead scores
    const submissions = (data || []).map((s: any) => ({
        ...s,
        lead_score: calculateLeadScore(s),
    }));

    return { data: submissions, total: count || 0 };
}

function calculateLeadScore(submission: any): number {
    let score = 0;

    // Form completeness (max 35 pts)
    if (submission.name) score += 10;
    if (submission.email) score += 10;
    if (submission.phone) score += 5;
    if (submission.country) score += 5;
    if (submission.budget_range) score += 5;

    // Form type bonus (apartment interest = more serious)
    if (submission.form_type === 'apartment_interest') score += 25;
    else score += 10; // waitlist

    // Device (desktop = typically more intentional)
    if (submission.device_type === 'desktop') score += 10;
    else score += 5;

    // Referral source
    if (submission.utm_source) score += 10;

    // Cap at 100
    return Math.min(score, 100);
}

// -------------------------------------------------------------------
// 3D Engagement Metrics
// -------------------------------------------------------------------

export async function fetchEngagementMetrics(days: number, propertyId?: string | null): Promise<EngagementMetrics> {
    const since = getSinceDate(days);

    let query = supabase
        .from('villa_visitor_sessions')
        .select('duration_seconds, hotspots_clicked, camera_movements, apartments_viewed, forms_opened, roof_toggles, hdr_changes')
        .gte('started_at', since);

    if (propertyId) {
        query = query.eq('property_id', propertyId);
    }

    const { data: sessions } = await query;

    const s = sessions || [];
    const total = s.length || 1;

    return {
        avgSessionDuration: s.reduce((sum: number, x: any) => sum + (x.duration_seconds || 0), 0) / total,
        totalHotspotClicks: s.reduce((sum: number, x: any) => sum + (x.hotspots_clicked || 0), 0),
        totalCameraMoves: s.reduce((sum: number, x: any) => sum + (x.camera_movements || 0), 0),
        totalApartmentsViewed: s.reduce((sum: number, x: any) => sum + (x.apartments_viewed || 0), 0),
        totalFormsOpened: s.reduce((sum: number, x: any) => sum + (x.forms_opened || 0), 0),
        totalRoofToggles: s.reduce((sum: number, x: any) => sum + (x.roof_toggles || 0), 0),
        totalHdrChanges: s.reduce((sum: number, x: any) => sum + (x.hdr_changes || 0), 0),
    };
}

// -------------------------------------------------------------------
// Conversion Funnel
// -------------------------------------------------------------------

export async function fetchFunnelData(days: number, propertyId?: string | null): Promise<FunnelData> {
    const since = getSinceDate(days);

    let query = supabase
        .from('villa_visitor_sessions')
        .select('is_engaged, forms_opened, is_converted')
        .gte('started_at', since);

    if (propertyId) {
        query = query.eq('property_id', propertyId);
    }

    const { data: sessions } = await query;

    const s = sessions || [];

    return {
        totalVisitors: s.length,
        engaged: s.filter((x: any) => x.is_engaged).length,
        formViewers: s.filter((x: any) => (x.forms_opened || 0) > 0).length,
        converted: s.filter((x: any) => x.is_converted).length,
    };
}
